import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, ShieldCheck } from "lucide-react";
import { api } from "./api";

// TEST-070: minimal publishing controls. All checks are enforced server-side (operator key + JRG_ENABLE_PUBLISH).
const LABEL = {
  not_published: ["Not published yet", "slate"],
  running: ["Publishing…", "amber"],
  verifying: ["Checking the live website…", "amber"],
  interrupted: ["Interrupted — safe to try again", "rose"],
  needs_reconciliation: ["Stopped part-way — hosting account check needed", "rose"],
  failed: ["Publishing failed", "rose"],
  uploaded: ["Website uploaded — not live yet", "amber"],
  dns_ssl_pending: ["Waiting for DNS / HTTPS", "amber"],
  live_failed: ["Live check failed", "rose"],
  verified_live: ["Verified live", "emerald"],
};
const TONE = { slate: "bg-slate-500/15 text-slate-300", amber: "bg-amber-500/15 text-amber-200",
  rose: "bg-rose-500/15 text-rose-200", emerald: "bg-emerald-500/15 text-emerald-200" };
const CAN_PUBLISH = ["not_published", "failed", "interrupted"];
const CAN_VERIFY = ["uploaded", "dns_ssl_pending", "live_failed", "verified_live"];

function StateLine({ st }) {
  const [label, tone] = LABEL[st.status] || [st.status, "slate"];
  return (
    <div className="space-y-2">
      <span data-testid="publish-state" className={`inline-block rounded-lg px-3 py-1.5 text-sm font-semibold ${TONE[tone]}`}>{label}</span>
      {st.domain && <p className="text-sm text-slate-400" data-testid="publish-domain">Domain: {st.domain}{st.version ? ` · version ${st.version}` : ""}</p>}
      {st.step && <p className="text-sm text-slate-400" data-testid="publish-step">{st.step}…{st.activeOn === "another server" ? " (running on another server)" : ""}</p>}
      {st.error && <p data-testid="publish-error" className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/30 px-3 py-2 text-sm text-rose-200 break-words">{st.error}</p>}
      {st.nextAction && <p data-testid="publish-next" className="text-sm text-slate-300">Next: {st.nextAction}</p>}
    </div>
  );
}

function PublishForm({ st, onStart, busy }) {
  const [domain, setDomain] = useState(st.domain || "");
  const [again, setAgain] = useState("");
  const [email, setEmail] = useState(st.suggestedEnquiryEmail || "");
  const [ok, setOk] = useState(false);
  const [contactOk, setContactOk] = useState(false);
  const c = st.contact || {};
  const input = "w-full rounded-lg bg-slate-950/60 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 focus:ring-amber-400 outline-none";
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onStart({ domain, domainConfirm: again, enquiryEmail: email, confirmEnquiry: ok, confirmContact: contactOk }); }}>
      <label className="block text-sm text-slate-300">Website domain (e.g. sweetalchemy.ie)
        <input data-testid="publish-domain-input" className={input} value={domain} onChange={(e) => setDomain(e.target.value)} autoComplete="off" /></label>
      <label className="block text-sm text-slate-300">Type the domain again
        <input data-testid="publish-domain-confirm" className={input} value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="off" /></label>
      <label className="block text-sm text-slate-300">Send website enquiries to
        <input data-testid="publish-enquiry-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      <label className="flex items-start gap-2 text-sm text-slate-300">
        <input data-testid="publish-enquiry-confirm" type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1" />
        I confirm enquiries should go to this address
      </label>
      <label className="flex items-start gap-2 text-sm text-slate-300" data-testid="publish-contact">
        <input data-testid="publish-contact-confirm" type="checkbox" checked={contactOk} onChange={(e) => setContactOk(e.target.checked)} className="mt-1" />
        <span>I checked the contact details shown on the website are correct: <b className="text-slate-100">{c.phone || "no phone"}</b> · <b className="text-slate-100">{c.email || "no email"}</b></span>
      </label>
      <button type="submit" disabled={busy || !domain || !again || !email || !ok || !contactOk} data-testid="publish-submit"
        className="flex items-center gap-2 rounded-xl bg-emerald-400 px-4 py-2.5 font-semibold text-slate-950 hover:bg-emerald-300 disabled:opacity-50 transition-colors">
        <Globe className="h-4 w-4" /> {busy ? "Starting…" : "Publish website"}
      </button>
    </form>
  );
}

export function PublishPanel({ jobId }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.publishStatus(jobId).then(setSt).catch(() => {}), [jobId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st || !["running", "verifying"].includes(st.status)) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st, load]);
  const act = async (fn) => {
    setBusy(true);
    try { setSt({ ...st, ...(await fn()) }); }
    catch (e) { toast.error(e.message + (e.nextAction ? ` — ${e.nextAction}` : "")); }
    finally { setBusy(false); load(); }
  };
  if (!st) return null;
  return (
    <section className="rounded-2xl bg-slate-900/60 ring-1 ring-slate-700 p-4 space-y-4" data-testid="publish-panel">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">Publish website</p>
      {!st.enabled && <p data-testid="publish-disabled" className="text-sm text-slate-400">Publishing is switched off for this environment.</p>}
      <StateLine st={st} />
      {st.enabled && st.status === "needs_reconciliation" && (
        <button onClick={() => act(() => api.reconcilePublish(jobId))} disabled={busy} data-testid="publish-reconcile"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-amber-500 text-slate-950 hover:bg-amber-400 transition-colors disabled:opacity-40">
          <ShieldCheck className="h-4 w-4" /> {busy ? "Checking…" : "Check hosting account"}
        </button>)}
      {st.enabled && CAN_PUBLISH.includes(st.status) && <PublishForm st={st} busy={busy} onStart={(b) => act(() => api.publish(jobId, b))} />}
      {st.enabled && CAN_VERIFY.includes(st.status) && (
        <button onClick={() => act(() => api.verifyLive(jobId))} disabled={busy} data-testid="verify-live"
          className="flex items-center gap-2 rounded-xl ring-1 ring-emerald-400/60 px-4 py-2.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
          <ShieldCheck className="h-4 w-4" /> {busy ? "Checking…" : "Verify live"}
        </button>
      )}
    </section>
  );
}
